interface Props {
  name: string
  audienceLabel: string
  fromAddress: string
  subject: string
  replyTo: string
  contentMode: 'group' | 'template' | 'html'
  selectedGroupId: string
  templateId: string
  htmlContent: string
  onBack: () => void
  onNext: () => void
}

export function CampaignStepReview({
  name,
  audienceLabel,
  fromAddress,
  subject,
  replyTo,
  contentMode,
  htmlContent,
  onBack,
  onNext,
}: Props): React.JSX.Element {
  const contentSummary =
    contentMode === 'group'
      ? 'WordPress Article Group (newspaper layout)'
      : contentMode === 'template'
        ? 'Template'
        : `Custom HTML (${htmlContent.length.toLocaleString()} chars)`

  function openPreview(): void {
    const win = window.open('', '_blank', 'width=1200,height=850')
    if (!win) return

    const escapedSubject = subject
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;')
    const escapedFrom = (fromAddress || 'noreply').replace(/&/g, '&amp;').replace(/</g, '&lt;')
    // Escape the email HTML for safe embedding in srcdoc attribute
    const escapedHtml = htmlContent.replace(/&/g, '&amp;').replace(/"/g, '&quot;')

    win.document.write(`<!DOCTYPE html><html><head>
<meta charset="utf-8"><title>Preview — ${escapedSubject}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; background:#111; color:#fff; height:100vh; display:flex; flex-direction:column; }
  .toolbar { display:flex; align-items:center; justify-content:space-between; padding:10px 20px; background:#1f1f1f; border-bottom:1px solid #333; flex-shrink:0; }
  .toolbar h1 { font-size:13px; font-weight:600; color:#ccc; }
  .toggle { display:flex; background:#333; border-radius:8px; padding:3px; gap:2px; }
  .toggle button { padding:6px 16px; border:none; border-radius:6px; font-size:12px; font-weight:600; cursor:pointer; transition:all 0.15s; background:transparent; color:#999; }
  .toggle button.active { background:#fff; color:#111; }
  .preview-area { flex:1; display:flex; justify-content:center; align-items:flex-start; overflow:auto; padding:20px; }
  .desktop-frame { width:100%; max-width:700px; background:#fff; border-radius:8px; overflow:hidden; box-shadow:0 4px 24px rgba(0,0,0,0.3); }
  .desktop-header { padding:14px 20px; border-bottom:1px solid #e5e7eb; background:#fafafa; }
  .desktop-header h2 { font-size:15px; font-weight:600; color:#111; margin-bottom:2px; }
  .desktop-header p { font-size:12px; color:#6b7280; }
  .desktop-body iframe { width:100%; min-height:700px; border:none; display:block; }
  .phone-frame { width:390px; background:#fff; border-radius:40px; overflow:hidden; border:7px solid #444; box-shadow:0 20px 60px rgba(0,0,0,0.5); }
  .phone-notch { height:30px; background:#000; border-bottom-left-radius:16px; border-bottom-right-radius:16px; width:120px; margin:0 auto; }
  .phone-status { display:flex; justify-content:space-between; padding:4px 24px 6px; font-size:12px; font-weight:600; color:#111; background:#f9fafb; }
  .phone-header { padding:10px 16px; border-bottom:1px solid #e5e7eb; background:#fff; }
  .phone-header h2 { font-size:14px; font-weight:600; color:#111; margin-bottom:1px; }
  .phone-header p { font-size:11px; color:#6b7280; }
  .phone-body { height:620px; overflow:auto; background:#fff; }
  .phone-body iframe { width:100%; height:100%; border:none; display:block; }
  .phone-home { height:5px; width:120px; background:#333; border-radius:3px; margin:8px auto; }
  .hidden { display:none !important; }
</style></head><body>
<div class="toolbar">
  <h1>${escapedSubject}</h1>
  <div class="toggle">
    <button id="btn-desktop" class="active" onclick="setMode('desktop')">Desktop</button>
    <button id="btn-mobile" onclick="setMode('mobile')">Mobile</button>
  </div>
</div>
<div class="preview-area">
  <div id="view-desktop" class="desktop-frame">
    <div class="desktop-header">
      <h2>${escapedSubject}</h2>
      <p>${escapedFrom} &middot; to me</p>
    </div>
    <div class="desktop-body">
      <iframe srcdoc="${escapedHtml}" sandbox="allow-same-origin"></iframe>
    </div>
  </div>
  <div id="view-mobile" class="phone-frame hidden">
    <div class="phone-notch"></div>
    <div class="phone-status"><span>9:41</span><span>5G &nbsp; ■</span></div>
    <div class="phone-header">
      <h2>${escapedSubject}</h2>
      <p>${escapedFrom}</p>
    </div>
    <div class="phone-body">
      <iframe srcdoc="${escapedHtml}" sandbox="allow-same-origin"></iframe>
    </div>
    <div class="phone-home"></div>
  </div>
</div>
<script>
function setMode(m) {
  document.getElementById('view-desktop').classList.toggle('hidden', m !== 'desktop');
  document.getElementById('view-mobile').classList.toggle('hidden', m !== 'mobile');
  document.getElementById('btn-desktop').classList.toggle('active', m === 'desktop');
  document.getElementById('btn-mobile').classList.toggle('active', m === 'mobile');
}
</script>
</body></html>`)
    win.document.close()
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <p className="text-sm text-text-secondary">Review everything before sending.</p>

      {/* Summary card */}
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-5">
        <h3 className="mb-3 text-sm font-medium uppercase tracking-wider text-text-secondary">
          Summary
        </h3>
        <dl className="space-y-2 text-sm">
          <SummaryRow label="Campaign" value={name} />
          <SummaryRow label="Audience" value={audienceLabel} />
          <SummaryRow label="From" value={fromAddress || 'Not set (required to send)'} />
          <SummaryRow label="Subject" value={subject} />
          {replyTo && <SummaryRow label="Reply-to" value={replyTo} />}
          <SummaryRow label="Content" value={contentSummary} />
        </dl>
      </div>

      {/* Preview button */}
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-text-primary">Email Preview</h3>
            <p className="mt-1 text-xs text-text-secondary">
              Opens in a new window with desktop/mobile toggle.
            </p>
          </div>
          <button
            type="button"
            onClick={openPreview}
            className="rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            Open Preview
          </button>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-surface-3 px-5 py-2.5 text-sm font-medium text-text-primary hover:bg-surface-1"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          Next: Send →
        </button>
      </div>
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="flex">
      <dt className="w-24 shrink-0 font-medium text-text-secondary">{label}</dt>
      <dd className="text-text-primary">{value}</dd>
    </div>
  )
}
